import { Check, X, Minus, ChevronDown } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const features = [
  { name: "AI Damage Detection", transportmate: true, enterprise: false, legacy: false, basic: false },
  { name: "Digital PoC/PoD with Signatures", transportmate: true, enterprise: true, legacy: true, basic: "partial" },
  { name: "Timestamped GPS Location", transportmate: true, enterprise: true, legacy: false, basic: false },
  { name: "Automated Invoicing", transportmate: true, enterprise: false, legacy: "partial", basic: false },
  { name: "QuickBooks/Xero/FreeAgent Sync", transportmate: true, enterprise: false, legacy: false, basic: false },
  { name: "Live Customer Tracking Link", transportmate: true, enterprise: true, legacy: false, basic: false },
  { name: "Offline Mode", transportmate: true, enterprise: false, legacy: true, basic: false },
  { name: "Mobile-First Design", transportmate: true, enterprise: "partial", legacy: "partial", basic: false },
  { name: "Multi-Driver Management", transportmate: true, enterprise: true, legacy: true, basic: "partial" },
  { name: "Vehicle Daily Checks (DVSA)", transportmate: true, enterprise: false, legacy: true, basic: false },
  { name: "Batch Invoicing", transportmate: true, enterprise: false, legacy: false, basic: false },
  { name: "No Long-Term Contracts", transportmate: true, enterprise: false, legacy: false, basic: "partial" },
];

const FeatureIcon = ({ value }: { value: boolean | string }) => {
  if (value === true) {
    return <Check className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />;
  }
  if (value === false) {
    return <X className="h-5 w-5 text-destructive/70" />;
  }
  return <Minus className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />;
};

// Simplified top features for mobile accordion
const mobileFeatures = [
  { name: "AI Damage Detection", tm: true, others: false },
  { name: "Automated Invoicing on Completion", tm: true, others: false },
  { name: "QuickBooks / Xero / FreeAgent Sync", tm: true, others: false },
  { name: "GPS-Stamped Digital Proof", tm: true, others: "partial" },
  { name: "Live Customer Tracking", tm: true, others: "partial" },
  { name: "Offline Mode", tm: true, others: "partial" },
  { name: "No Long-Term Contracts", tm: true, others: false },
];

export function CompetitorComparison() {
  const animation = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      ref={animation.ref as React.RefObject<HTMLElement>}
      className={`container mx-auto px-4 sm:px-4 py-10 sm:py-16 transition-all duration-1000 ${
        animation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
      }`}
    >
      <div className="text-center mb-6 sm:mb-12 px-2">
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-4">
          How We Compare
        </h2>
        <p className="text-sm sm:text-lg text-muted-foreground max-w-2xl mx-auto">
          Purpose-built for independent UK operators
        </p>
      </div>

      {/* Mobile: Accordion comparison */}
      <div className="sm:hidden">
        <div className="bg-card border rounded-xl overflow-hidden">
          <div className="p-4 border-b bg-primary/5">
            <div className="flex justify-between items-center text-sm font-semibold">
              <span>Feature</span>
            <div className="flex gap-4">
                <span className="text-primary text-[11px]">TransportMate</span>
                <span className="text-muted-foreground text-[11px]">Alternatives</span>
              </div>
            </div>
          </div>
          <div className="divide-y">
            {mobileFeatures.map((f) => (
              <div key={f.name} className="flex justify-between items-center p-3.5">
                <span className="text-sm font-medium flex-1 pr-4">{f.name}</span>
                <div className="flex gap-4 flex-shrink-0">
                  <FeatureIcon value={f.tm} />
                  <FeatureIcon value={f.others} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="mt-3 flex justify-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Check className="h-3.5 w-3.5 text-emerald-600" /> Yes</span>
          <span className="flex items-center gap-1"><Minus className="h-3.5 w-3.5 text-yellow-600" /> Partial</span>
          <span className="flex items-center gap-1"><X className="h-3.5 w-3.5 text-destructive/70" /> No</span>
        </div>
      </div>

      {/* Desktop: Full table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse bg-card rounded-xl border shadow-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left p-4 font-semibold">Feature</th>
              <th className="p-4 text-center">
                <span className="font-bold text-primary">TransportMate</span>
              </th>
              <th className="p-4 text-center">
                <span className="font-medium text-muted-foreground">Enterprise<br/>Systems</span>
              </th>
              <th className="p-4 text-center">
                <span className="font-medium text-muted-foreground">Legacy<br/>Software</span>
              </th>
              <th className="p-4 text-center">
                <span className="font-medium text-muted-foreground">Basic<br/>Tools</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature, index) => (
              <tr key={feature.name} className={index % 2 === 0 ? "bg-muted/30" : "bg-card"}>
                <td className="p-4 font-medium">{feature.name}</td>
                <td className="p-4 text-center"><div className="flex justify-center"><FeatureIcon value={feature.transportmate} /></div></td>
                <td className="p-4 text-center"><div className="flex justify-center"><FeatureIcon value={feature.enterprise} /></div></td>
                <td className="p-4 text-center"><div className="flex justify-center"><FeatureIcon value={feature.legacy} /></div></td>
                <td className="p-4 text-center"><div className="flex justify-center"><FeatureIcon value={feature.basic} /></div></td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-6 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
          <div className="flex items-center gap-2"><Check className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /><span>Fully supported</span></div>
          <div className="flex items-center gap-2"><Minus className="h-4 w-4 text-yellow-600 dark:text-yellow-400" /><span>Partial / Limited</span></div>
          <div className="flex items-center gap-2"><X className="h-4 w-4 text-destructive/70" /><span>Not available</span></div>
        </div>
      </div>

      <p className="mt-4 sm:mt-6 text-center text-xs text-muted-foreground max-w-2xl mx-auto">
        Comparison reflects typical capabilities across software categories in the UK vehicle transport industry.
      </p>
    </section>
  );
}
